import React from 'react'
import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom'
import { Navbar } from '@structura/ui'
import { useTranslation } from '@structura/i18n'
import { ProtectedRoute, AdminRoute, Register, PendingActivation, ForgotPassword, ResetPassword, VerifyEmail, AccountPage } from '@structura/auth'
import { StructuraBitsList, ArticleView } from '@structura/structura-bits'
import { HomePage, AboutPage } from '@structura/pages'
import { SharedView, LinkView, SharedWithMePanel } from '@structura/sharing'
import { sharingClient, renderArtifact, typeLabel, RouterLink } from './sharing.config'
import Dashboard from './pages/Dashboard'
import WoodBeam from './pages/WoodBeam/index'
import Admin from './pages/Admin'
import packageJson from '../package.json'

// Wood beam designer app. Standalone — its own shell, routes and navigation;
// the only beam route is /beam/wood.
const BEAM_ROUTE = '/beam/wood'

// App-owned route wrappers: read the route param and pass it to the shared package.
const SharedRoute: React.FC = () => {
  const { shareId } = useParams<{ shareId: string }>()
  return <SharedView shareId={shareId ?? ''} client={sharingClient} renderArtifact={renderArtifact} LinkComponent={RouterLink} />
}

const LinkRoute: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  return <LinkView token={token ?? ''} client={sharingClient} renderArtifact={renderArtifact} productName="Wood" />
}

const DeltPage: React.FC = () => (
  <div className="sm-page">
    <div className="sm-container">
      <div className="sm-page-header"><h1>Delt med meg</h1></div>
      <SharedWithMePanel client={sharingClient} typeLabel={typeLabel} LinkComponent={RouterLink} />
    </div>
  </div>
)

const App: React.FC = () => {
  const { t } = useTranslation()
  const location = useLocation()
  // The anonymous capability-link page renders standalone (no app navbar).
  if (location.pathname.startsWith('/link/')) {
    return (
      <Routes>
        <Route path="/link/:token" element={<LinkRoute />} />
      </Routes>
    )
  }
  return (
    <div className="App">
      <Navbar beamRoute={BEAM_ROUTE} beamLabel={t('app.wood.beamLabel')} />
      <main>
        <Routes>
          <Route path="/" element={<HomePage dashboard={<Dashboard />} />} />
          {/* Legacy /dashboard URL still in browser histories + bookmarks —
              redirect to / where Home now renders the dashboard inline. */}
          <Route path="/dashboard" element={<Navigate to="/" replace />} />
          <Route path={BEAM_ROUTE} element={<ProtectedRoute><WoodBeam /></ProtectedRoute>} />
          <Route path="/smbeam-designer" element={<Navigate to={BEAM_ROUTE} replace />} />
          <Route path="/structura-bits" element={<StructuraBitsList material="wood" />} />
          <Route path="/structura-bits/:slug" element={<ArticleView />} />
          <Route path="/about" element={<AboutPage appVersion={packageJson.version} beamRoute={BEAM_ROUTE} />} />
          <Route path="/register" element={<Register beamRoute={BEAM_ROUTE} />} />
          <Route path="/account" element={<AccountPage appLabel="Wood" />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* Norwegian paths used by the central auth service's e-mails */}
          <Route path="/tilbakestill-passord" element={<ResetPassword />} />
          <Route path="/verifiser-epost" element={<VerifyEmail />} />
          <Route path="/pending-activation" element={<PendingActivation />} />
          <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
          <Route path="/delt" element={<ProtectedRoute><DeltPage /></ProtectedRoute>} />
          <Route path="/shared/:shareId" element={<ProtectedRoute><SharedRoute /></ProtectedRoute>} />
        </Routes>
      </main>
    </div>
  )
}

export default App
